import axios from 'axios';
import { CLIENT_INSTANCE_HEADER, getClientInstanceId } from './clientInstance';

export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// 🔒 REQUEST interceptor, not response — and unconditional, so it is present on
// the LOGIN post, which is unauthenticated. That request is the one that claims
// a device slot; without the header there, two browsers on the same OS and
// browser version are still one device at the only moment it matters.
// Header omitted entirely when the browser cannot store a marker: the backend
// then hashes exactly as it did before this header existed.
api.interceptors.request.use((config) => {
  try {
    const id = getClientInstanceId();
    if (id) {
      config.headers.set(CLIENT_INSTANCE_HEADER, id);
    }
  } catch {
    // Never let device identification break a request.
  }
  return config;
});

/**
 * 🔒 PAGES A LOGGED-OUT PERSON MUST BE ABLE TO STAY ON. Any background call
 * that answers 401 used to send EVERY page except /login to /login — so the
 * reset link in the password email (/reset-password?token=…) bounced to the
 * login page for exactly the people who need it: the ones who are logged out.
 * Found 2026-10-07. Keep in step with the public <Route>s in App.tsx.
 */
const PUBLIC_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/logout'];
const PUBLIC_PREFIXES = ['/portal/', '/payment-confirmation/', '/trip-locate/'];

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.includes(pathname) || PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

api.interceptors.response.use(
  (res) => {
    // Apr 23, 2026 (Bug 7) — auto-refresh credit chip whenever any AI feature
    // or an explicit /credits/deduct call succeeds. Keeps TopBar in sync
    // without every call site having to remember to bump the bus.
    try {
      const method = (res.config?.method ?? '').toLowerCase();
      const url = res.config?.url ?? '';
      const isMutating = method === 'post' || method === 'put' || method === 'patch';
      if (isMutating && (url.startsWith('/ai/') || url.includes('/credits/deduct'))) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('aura:credits:refresh'));
        }
      }
    } catch {
      // Ignore — this is best-effort UX sugar, not critical.
    }
    return res;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login unless already on a page that needs no login
      if (!isPublicPath(window.location.pathname)) {
        window.location.href = '/login';
      }
    }

    // 402 — Kiaan subscription required. Dispatch a custom event so the AIPanel
    // can render the KiaanLockedCard without every call site needing to handle it.
    if (
      error.response?.status === 402 &&
      error.response?.data?.error === 'kiaan_subscription_required'
    ) {
      try {
        const payload = error.response.data;
        window.dispatchEvent(
          new CustomEvent('aura:kiaan:locked', {
            detail: {
              error: payload.error,
              message: payload.message ?? 'Subscribe to Kiaan to unlock AI features.',
              ctaUrl: payload.ctaUrl ?? 'https://kiaan.ai',
              ctaLabel: payload.ctaLabel ?? 'Explore Kiaan',
            },
          })
        );
      } catch {
        // Best-effort — don't break the error chain.
      }
    }

    return Promise.reject(error);
  }
);

// ── Manufacturing / Production planning helpers ─────────────────────────────
// Thin wrappers so pages don't hard-code endpoints. Safe to evolve as the
// backend adds bucketed responses — we fall back to client-side splitting
// when the API still returns a flat list.

export type PlanningBucket = 'today' | 'tomorrow' | 'thisWeek' | 'backlog';

export async function getProductionPlan(params?: { bucket?: PlanningBucket }) {
  const res = await api.get('/manufacturing-flow/planning', {
    params: params?.bucket ? { bucket: params.bucket } : undefined,
  });
  return res.data?.data ?? res.data;
}

export async function startShift(payload: { orderIds?: string[]; productionOrderIds?: string[] } = {}) {
  // Backend contract: POST /manufacturing-p0/shift/start { productionOrderIds: string[] }
  const productionOrderIds =
    payload.productionOrderIds ?? payload.orderIds ?? [];
  const res = await api.post('/manufacturing-p0/shift/start', { productionOrderIds });
  return res.data?.data ?? res.data;
}
